import React, { useEffect, useRef, useCallback } from 'react';
import { IntakeFormData, BRAND_PERSONALITIES } from '../../types/form';

interface Props {
  data: IntakeFormData;
  onChange: (updates: Partial<IntakeFormData>) => void;
}

const UPLOADCARE_KEY = 'ec6982ef05d0a29b6d09';

const inputClass = `
  w-full bg-dark-700 border border-dark-500 rounded-lg px-4 py-3
  text-white text-sm placeholder-gray-600
  focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400/20
  transition-all duration-200
`;

const labelClass = "block text-xs font-medium text-gray-300 uppercase tracking-wider mb-2";

declare global {
  interface Window {
    uploadcare: any;
  }
}

const Step3Brand: React.FC<Props> = ({ data, onChange }) => {
  const widgetRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const togglePersonality = (p: string) => {
    const current = data.brandPersonality;
    if (current.includes(p)) {
      onChange({ brandPersonality: current.filter(x => x !== p) });
    } else if (current.length < 3) {
      onChange({ brandPersonality: [...current, p] });
    }
  };

  const initWidget = useCallback(() => {
    if (!inputRef.current || !window.uploadcare) return;
    const widget = window.uploadcare.Widget(inputRef.current);
    widgetRef.current = widget;

    widget.onChange((group: any) => {
      if (!group) {
        onChange({ uploadedFiles: [] });
        return;
      }
      group.promise().then((info: any) => {
        const urls: string[] = [];
        for (let i = 0; i < info.count; i++) {
          info.files()[i].done((fileInfo: any) => {
            urls.push(fileInfo.cdnUrl);
            if (urls.length === info.count) {
              onChange({ uploadedFiles: urls });
            }
          });
        }
      });
    });
  }, [onChange]);

  useEffect(() => {
    if (!document.getElementById('uploadcare-script')) {
      const script = document.createElement('script');
      script.id = 'uploadcare-script';
      script.src = 'https://ucarecdn.com/libs/widget/3.x/uploadcare.full.min.js';
      script.async = true;
      script.onload = () => initWidget();
      document.head.appendChild(script);
    } else if (window.uploadcare) {
      initWidget();
    }

    return () => {
      widgetRef.current = null;
    };
  }, [initWidget]);

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h2 className="font-display text-2xl font-semibold text-white mb-1">
          Brand & assets.
        </h2>
        <p className="text-sm text-gray-300">Help us understand your brand direction and upload any files we should work from.</p>
      </div>

      {/* Logo */}
      <div>
        <label className={labelClass}>Do you have an existing logo?</label>
        <div className="flex gap-3 flex-wrap">
          {['Yes', 'No', 'Needs a redesign'].map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange({ hasLogo: opt })}
              className={`
                px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200
                ${data.hasLogo === opt
                  ? 'border-lime-400 bg-lime-400/10 text-lime-400'
                  : 'border-dark-500 bg-dark-700 text-gray-400 hover:border-dark-400'
                }
              `}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Brand colors */}
      <div>
        <label className={labelClass}>Brand Colors</label>
        <input
          type="text"
          className={inputClass}
          placeholder="e.g. Navy blue, gold, white — or hex codes if you have them"
          value={data.brandColors}
          onChange={e => onChange({ brandColors: e.target.value })}
        />
      </div>

      {/* Brand personality */}
      <div>
        <label className={labelClass}>Brand Personality (pick up to 3)</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {BRAND_PERSONALITIES.map(p => {
            const selected = data.brandPersonality.includes(p);
            const maxed = data.brandPersonality.length >= 3 && !selected;
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePersonality(p)}
                disabled={maxed}
                className={`
                  px-3 py-2 rounded-lg border text-xs font-medium transition-all duration-200
                  ${selected
                    ? 'border-lime-400 bg-lime-400/10 text-lime-400'
                    : maxed
                    ? 'border-dark-600 bg-dark-800 text-gray-700 cursor-not-allowed'
                    : 'border-dark-500 bg-dark-700 text-gray-400 hover:border-dark-400'
                  }
                `}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Competitors */}
      <div>
        <label className={labelClass}>Competitor Websites</label>
        <input
          type="text"
          className={inputClass}
          placeholder="List any competitor sites (comma separated)"
          value={data.competitorSites}
          onChange={e => onChange({ competitorSites: e.target.value })}
        />
      </div>

      {/* Inspiration */}
      <div>
        <label className={labelClass}>Design Inspiration</label>
        <input
          type="text"
          className={inputClass}
          placeholder="Sites or brands you love the look of"
          value={data.inspiration}
          onChange={e => onChange({ inspiration: e.target.value })}
        />
      </div>

      {/* Uploadcare file uploader */}
      <div>
        <label className={labelClass}>Upload Your Files</label>
        <p className="text-xs text-gray-500 mb-3">
          Logos, photos, brand guides, inspiration images — anything useful. Multiple files supported.
        </p>

        <input
          ref={inputRef}
          type="hidden"
          data-public-key={UPLOADCARE_KEY}
          data-multiple="true"
          data-preview-step="true"
          data-clearable="true"
        />

        {data.uploadedFiles.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-lime-400 font-medium">
              {data.uploadedFiles.length} file{data.uploadedFiles.length > 1 ? 's' : ''} uploaded successfully
            </p>
            {data.uploadedFiles.map((url, i) => (
              <div key={i} className="flex items-center gap-2 bg-dark-700 border border-dark-500 rounded-lg px-3 py-2">
                <svg className="w-4 h-4 text-lime-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-300 truncate hover:text-lime-400 transition-colors">
                  File {i + 1}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .uploadcare--widget__button {
          background: #1a1a1a !important;
          border: 1px solid #2e2e2e !important;
          color: #9ca3af !important;
          border-radius: 8px !important;
          padding: 10px 16px !important;
          font-size: 13px !important;
          cursor: pointer !important;
          transition: all 0.2s !important;
          font-family: 'Outfit', sans-serif !important;
        }
        .uploadcare--widget__button:hover {
          border-color: #C8F135 !important;
          color: #C8F135 !important;
        }
        .uploadcare--widget__progress {
          background: #C8F135 !important;
        }
      `}</style>
    </div>
  );
};

export default Step3Brand;
